import { isIpInCidr } from "../libs/utils"

/**
 * API list 요청 시 공통적으로 반환되는 형태를 정의한 인터페이스.
 */
export interface ApiListStructure<T> {
  items: T[]
  totalCount: number
  /**
   * 없을 수도 있는 값
   */
  message?: string
  hasNext?: boolean
}

/**
 * 이전의 ApiCUDStructure 를 대체
 */
export interface ApiSimpleResponseStructure {
  /**
   * allow로 보냈다면 true, deny로 보냈다면 false
   */
  allowed: boolean
  code?: number
  /**
   * 설정한 메시지를 받으며, 아무것도 설정하지 않았다면 null로 오게 된다
   */
  message?: string
}

/**
 * 백엔드에서 만들어지는 공통의 이미지 객체
 */
export interface ImageSet {
  dataSet: string
  original: string
  thumbnail: string
  thumbnail_l: string
  thumbnail_m: string
  thumbnail_s: string
}
export type ImageSetFields = keyof ImageSet | '$all'

export interface DYBankAccount {
  bank: string
  number: string
  owner: string
}

export function convertErrorCode(code: number) {
  switch (code) {
    case 100: return 'Continue';
    case 101: return 'Switching Protocols';
    case 200: return 'OK';
    case 201: return 'Created';
    case 202: return 'Accepted';
    case 203: return 'Non-Authoritative Information';
    case 204: return 'No Content';
    case 205: return 'Reset Content';
    case 206: return 'Partial Content';
    case 300: return 'Multiple Choices';
    case 301: return 'Moved Permanently';
    case 302: return 'Moved Temporarily';
    case 303: return 'See Other';
    case 304: return 'Not Modified';
    case 305: return 'Use Proxy';
    case 400: return 'Bad Request';
    case 401: return '해당 페이지에 대한 권한이 없습니다.(Unauthorized)';
    case 402: return 'Payment Required';
    case 403: return '해당 페이지에 대한 권한이 없습니다.(Forbidden)';
    case 404: return '해당 페이지를 찾을 수 없습니다.';
    case 405: return 'Method Not Allowed';
    case 406: return 'Not Acceptable';
    case 407: return 'Proxy Authentication Required';
    case 408: return 'Request Time-out';
    case 409: return 'Conflict';
    case 410: return 'Gone';
    case 411: return 'Length Required';
    case 412: return 'Precondition Failed';
    case 413: return 'Request Entity Too Large';
    case 414: return 'Request-URI Too Large';
    case 415: return 'Unsupported Media Type';
    case 500: return '해당 페이지에서 알 수 없는 오류가 발생하여 정상적으로 열 수 없습니다.';
    case 501: return 'Not Implemented';
    case 502: return 'Bad Gateway';
    case 503: return 'Service Unavailable';
    case 504: return 'Gateway Time-out';
    case 505: return 'HTTP Version not supported';
    default: return '에러'
  }
}

/**
 * 사내 IP 인가. `ip` 는 서버가 알려준 값(`GET /api/ip`, SSR 통계)을 쓴다.
 *
 * ## ⚠️ PHP `UtilityFunctions::getArrCompanyIp()` 와 맞춰야 한다
 *
 * 2026-08-25 부터 로그인 폼이 이 판정으로 **Turnstile 위젯을 띄울지**를 정한다
 * (`DYLoginPage.useTurnstile`). 불일치의 위험은 **한쪽 방향뿐**이다.
 *
 *   · 여기서만 사내로 판단 → 위젯 없음 → 토큰 없음 → **게이트 강제 시 401** 🔴
 *   · PHP 에서만 사내로 판단 → 위젯 뜸 → 토큰 보냄 → 서버가 면제 → 무해
 *
 * **즉 이 목록이 PHP 보다 넓으면 안 된다.**
 *
 * 아래 `192.168.*` 4개는 PHP 에 없지만 **문제되지 않는다**(2026-08-25 확인).
 * 사내 공유기가 내주는 LAN IP 인데, `ip` 는 `GET /api/ip` 가 돌려주는 **공인 IP** 라
 * 운영에서 매칭될 일이 없다. 로컬 개발은 `process.dev` 가 따로 덮는다.
 * → 공인 IP 를 추가·삭제할 때만 PHP 와 맞추면 된다.
 */
export function isCompanyIp(ip: string) {
  return [
    '121.66.53.58',
    '121.66.53.59',
    '121.66.53.61',
    '121.66.53.62',
    '114.207.244.47',
    '118.218.219.139', // 웹서버 (2026-10-02 IP 변경, 구 1.234.21.191 제거)
    '112.220.197.86',
    '192.168.0.56', // 명진PD 폰
    '192.168.0.70',
    '192.168.0.75',
    '192.168.0.76'
  ].includes(ip)
}

/**
 * 네이버 크롤러 아이피 대역인지 확인
 * https://searchadvisor.naver.com/doc/naverbot.json
 */
export function isNaverBotIp(ip: string) {
  const naverBotCidrs = [
    "110.93.146.32/29",
    "110.93.150.21/32",
    "110.93.150.22/31",
    "110.93.150.24/29",
    "110.93.150.32/27",
    "110.93.150.64/26",
    "110.93.150.128/26",
    "110.93.150.192/28",
    "110.93.150.208/29",
    "110.93.150.216/30",
    "110.93.150.220/32",
    "114.111.32.21/32",
    "114.111.32.22/31",
    "114.111.32.24/29",
    "114.111.32.32/27",
    "114.111.32.64/26",
    "114.111.32.128/26",
    "114.111.32.192/28",
    "114.111.32.208/29",
    "114.111.32.216/30",
    "114.111.32.220/32",
    "125.209.235.167/32",
    "125.209.235.168/29",
    "125.209.235.176/29",
    "125.209.235.184/31",
    "125.209.235.186/32",
    "211.249.46.21/32",
    "211.249.46.22/31",
    "211.249.46.24/29",
    "211.249.46.32/27",
    "211.249.46.64/26",
    "211.249.46.128/26",
    "211.249.46.192/28",
    "211.249.46.208/29",
    "211.249.46.216/30",
    "211.249.46.220/32"
  ]
  
  return naverBotCidrs.some(cidr => isIpInCidr(ip, cidr))
}

/**
 * 다음(카카오) 검색 수집로봇의 User-Agent 인지 확인한다.
 *
 * 2026-09-23 [센트리 NUXT3-MYFIRSTGUIDE-F7 조사 중 발견] 종전 `isHuman` 은 `userAgent.includes('Daum')` 으로
 *   다음 봇을 걸렀는데, 이 조건이 **다음 앱 인앱 브라우저**(`... DaumApps/9.9.1 DaumDevice/mobile`)에도 걸려
 *   다음 앱으로 들어온 사람 전원이 봇으로 판정되고 있었다(2024-03 이전부터).
 *   → 앱 초기화(`init()`)·로그인 복원·방문 통계·광고 유입 기록이 전부 빠지고 크롤러용 화면을 받았다.
 *   (실측: 운영 페이지를 다음 앱 UA 로 요청하면 `class="isBot"`, 접속 로그에 다음 앱 방문 0건)
 *
 * 그래서 "Daum" 이라는 **글자**가 아니라 로봇의 **이름+버전 모양**으로 판정한다.
 *   관측된 다음 봇 UA (udger·user-agents.net 수집본):
 *     `(compatible; Daum/4.1; +http://cs.daum.net/...)`         ← 슬래시형
 *     `... Safari/538.1 Daum/4.1`                                ← 렌더러형
 *     `(compatible; MSIE or Firefox mutant;) Daum 4.1`           ← 🔴 **공백형**(2025-06 관측) — `includes('Daum/')` 로는 놓친다
 *     `... Daumoa/4.0`, `... Daumoa 4.0`, `... Daumoa-image/1.0` ← 구형 이름(Daumoa)
 *     `daumoa,damoa,daum,daumos,...`                             ← 이름 나열형
 *     `... PhantomJS/1.9.7 Daum Raam Edition ...`                ← 옛 렌더러(2016 이후 미관측)
 *   🔴 사람 쪽 = `DaumApps/<버전>` · `DaumDevice/mobile` — "Daum" 바로 뒤가 숫자가 아니므로 걸리지 않는다.
 *   ⚠️ UA 만으로 본다(위조 가능). `isHuman` 은 보안 판정이 아니라 «크롤러용으로 그릴지» 판정이라 충분하다.
 */
export function isDaumCrawlerUa(userAgent: string) {
  return /\bdaum(oa)?(-image)?[\/ ]?\d/i.test(userAgent) // Daum/4.1 · Daum 4.1 · Daumoa/4.0 · Daumoa 4.0 · Daumoa-image/1.0
    || /^daumoa,/i.test(userAgent) // daumoa,damoa,daum,... (이름 나열형)
    || /\bDaum Raam\b/.test(userAgent) // 옛 렌더러
}

/**
 * 데스크톱 리눅스·크롬OS **브라우저**의 User-Agent 인지 확인한다(`isHuman` 의 사람 판정용).
 *
 * 2026-10-06 [센트리 NUXT3-DOYACWEB-10E / NUXT3-DOYACART-QP 조사 중 발견] `isHuman` 은 봇 목록에 안 걸려도
 *   마지막에 «윈도우·맥·안드로이드·iOS·앱» 중 하나여야 사람으로 봤다. 그래서 리눅스 데스크톱 크롬·파이어폭스와
 *   크롬북(크롬OS) 사용자는 **사람인데도 봇 화면**(우측 패널·푸터·배너·지연 로딩 구간 없음, 방문 통계 누락)을 받았다.
 *   미공개 글에 들어오면 SSR 이 본문을 그린 채 클라에서 Nuxt `showError()` 로 화면을 갈아끼우다
 *   `Cannot destructure property 'el' of 'R' as it is null` 로 터지고 **미공개 본문이 화면에 남았다**
 *   (운영 재현: 같은 URL 을 리눅스 UA 로 열면 매번, 윈도우 UA 로 열면 0번).
 *
 * 판정 = UA 플랫폼 칸이 `(X11; …` 로 시작하고 실제 브라우저 토큰이 있는 것. 실측 모양:
 *   크롬·엣지  `(X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/145.0.0.0 Safari/537.36`
 *   크롬OS     `(X11; CrOS x86_64 14541.0.0) AppleWebKit/537.36 … Chrome/…`
 *   파이어폭스 `(X11; Linux x86_64; rv:130.0) Gecko/20100101 Firefox/130.0` · `(X11; Ubuntu; Linux x86_64; rv:…)`
 *   ⚠️ 안드로이드 크롬의 «데스크톱 사이트» 모드도 `(X11; Linux x86_64)` 로 위장한다 — 그 경우도 사람이 맞으므로 함께 들어온다
 *      (종전엔 SSR 의 Sec-CH-UA-Platform 보정·DYDefault 의 터치 보정이 isAndroidOS 를 켜야만 사람이 됐다).
 *   ⚠️ 스마트TV(`Web0S; Linux/SmartTV`·`SMART-TV; LINUX; Tizen`)는 `X11` 이 없어 **종전대로** 사람 판정 밖이다.
 *
 * 🔴 리눅스는 서버형 크롤러가 가장 많이 쓰는 OS 라, 다른 OS 와 달리 **흔한 봇 자기표식이 있으면 들이지 않는다**
 *   (`bot`·`crawl`·`spider`·`headless`·`python`·`curl` 등). 표식 없이 사람 UA 를 그대로 흉내 내는 크롤러
 *   (실례: 헤츠너 클라우드의 리눅스 Chrome 145)는 사람으로 들어오게 되는데, 윈도우 UA 를 쓰는 같은 부류가 이미
 *   그렇게 들어오고 있으므로 새 구멍은 아니다. 검색엔진 봇은 `isHuman` 의 명시 목록이 먼저 거른다.
 * ⚠️ UA 만으로 본다(위조 가능). `isHuman` 은 보안 판정이 아니라 «크롤러용으로 그릴지» 판정이다.
 */
export function isDesktopLinuxBrowserUa(userAgent: string) {
  if (!/\(X11;/.test(userAgent)) return false // 데스크톱 리눅스·크롬OS 의 플랫폼 표기
  if (!/(Chrome|Chromium|Firefox|Safari)\//.test(userAgent)) return false // 실제 브라우저 토큰
  return !/bot|crawl|spider|scrap|slurp|headless|phantom|selenium|puppeteer|playwright|python|curl|wget|http-?client|java\//i.test(userAgent)
}
